import React from 'react';
import { Modal, Button, Form, OptionalSection, Select, Icon, TooltipHelp, Section, ToggleContainer, TranslateDropdown, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Styles from '../../../../../styles/form/modalAddFields.module.css';
import { requiredField, useForm } from '../../../../../utils/validators';
import { capitalizeFirstLetter } from '../../../../attendee/detail/components/utils';
import PropTypes from 'prop-types';
import SelectFieldForm from './selectFieldForm';
import FormEntryModel from '../../../../../models/form_entry';
import { usePermissions } from '../../../../../user/permissions';
import { useSegment } from '../../../../../components/tracking/trackingProvider';
import useAppContext from '../../../../../context/useAppContext';

function FieldNameInput({ form, formErrors, defaultLanguage, languageOptions, handleLanguage, handleTranslations }) {
    const { t } = useTranslation();
    const [selectedNameLanguage, setSelectedNameLanguage] = React.useState(defaultLanguage);

    const fieldNameValue = React.useMemo(() => {
        return (!Object.keys(form.translationsLabel) || selectedNameLanguage === defaultLanguage ? form.label : form.translationsLabel[selectedNameLanguage]) || '';
    }, [defaultLanguage, form, selectedNameLanguage]);

    return (
        <Form.Field>
            <Form.Label>
                <div className={Styles['field-name-input']}>
                    <span>
                        {t('ticket.common.fieldName')}
                        <TooltipHelp title={t('ticket.form.modal.customFields.FieldNameTooltip')} />
                    </span>
                    <TranslateDropdown
                        defaultValue={defaultLanguage}
                        languages={languageOptions}
                        onChange={value => {
                            setSelectedNameLanguage(value);
                            handleLanguage(value, 'label');
                        }}
                        selected={Object.keys(form.translationsLabel)}
                    />
                </div>
            </Form.Label>
            <Form.Input
                placeholder={form.field_type ? t(`ticket.common.fieldType.${form.field_type}`) : t('ticket.common.fieldName')}
                value={fieldNameValue}
                onChange={({ target: { value } }) => handleTranslations(value, 'label', selectedNameLanguage)}
            />
            <Form.FieldErrors errors={[formErrors['label'] ? t(formErrors['label']) : null]} />
        </Form.Field>
    );
}

function FieldDescriptionInput({ form, defaultLanguage, languageOptions, handleTranslations, handleLanguage }) {
    const { t } = useTranslation();
    const [selectedDescriptionLanguage, setSelectedDescriptionLanguage] = React.useState(defaultLanguage);

    const fieldDescriptionValue = React.useMemo(() => {
        return (
            (!Object.keys(form.translationsDescription) || selectedDescriptionLanguage === defaultLanguage
                ? form.description
                : form.translationsDescription[selectedDescriptionLanguage]) || ''
        );
    }, [defaultLanguage, form, selectedDescriptionLanguage]);

    return (
        <Form.Field style={{ marginTop: '0' }}>
            <Form.Label>
                <div className={Styles['field-name-input']}>
                    <span>
                        {t('ticket.form.modal.customFields.descriptionLabel')}
                        <TooltipHelp title={t('ticket.form.modal.customFields.descriptionTooltip')} />
                    </span>
                    <TranslateDropdown
                        defaultValue={defaultLanguage}
                        languages={languageOptions}
                        onChange={value => {
                            setSelectedDescriptionLanguage(value);
                            handleLanguage(value, 'description');
                        }}
                        selected={Object.keys(form.translationsDescription)}
                    />
                </div>
            </Form.Label>
            <Form.Sublabel label={t('ticket.form.modal.customFields.descriptionSublabel')} />
            <Form.TextArea
                style={{ resize: 'none' }}
                showSizes
                maxlength={800}
                placeholder={t('ticket.form.modal.customFields.descriptionPlaceholder')}
                onChange={({ target: { value } }) => handleTranslations(value, 'description', selectedDescriptionLanguage)}
                value={fieldDescriptionValue}
            />
        </Form.Field>
    );
}
export default function ModalCustomField({
    formEntry = null,
    showModal = false,
    customTypes = [],
    usage = null,
    onAddField = () => {},
    onClose = () => {},
    onCloseAllModals = () => {},
    defaultLanguage,
    languageOptions
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const permissions = usePermissions();

    const initialForm = {
        is_standard: false,
        label: null,
        field_type: null,
        default_value: null,
        question_choices: [],
        description: null,
        translationsLabel: {},
        translationsDescription: {}
    };

    const [form, setForm, setFormObject, formErrors, formValidate, setErrors] = useForm(
        { ...initialForm },
        {
            label: requiredField,
            field_type: requiredField
        }
    );

    const handleLanguage = React.useCallback(
        (value, name) => {
            let _form = { ...form };
            let translations = _form[`translations${capitalizeFirstLetter(name)}`];

            // Set default value upon language selection
            if (!(value in translations)) {
                translations[value] = '';
            }

            // Delete empty keys
            Object.keys(translations)
                .filter(k => k !== value)
                .forEach(key => {
                    if (translations[key] === '' && translations[key] !== null) {
                        delete translations[key];
                    }
                });
            _form[`translations${name}`] = translations;
            setFormObject({ ..._form }, true);
        },
        [form, setFormObject]
    );

    const cleanTranslations = React.useCallback(
        trans => {
            let transToApply = { ...trans };
            if (
                Object.keys(trans).length > 1 &&
                Object.entries(trans)
                    .filter(k => k[0] !== defaultLanguage)
                    .every(key => key[1] === null)
            ) {
                transToApply[defaultLanguage] = null;
            }

            return transToApply;
        },
        [defaultLanguage]
    );

    const handleTranslations = React.useCallback(
        (value, property, selectedLanguage) => {
            // Set both name and description values, as well as translations
            if (property === 'label' && value.length >= 255) {
                return;
            }
            let _form = { ...form };
            let trans = _form[`translations${capitalizeFirstLetter(property)}`];
            trans[selectedLanguage] = value;
            if (selectedLanguage !== defaultLanguage) {
                if (!value && property === 'label') {
                    trans[selectedLanguage] = null;
                }
            } else {
                _form[property] = value;
            }
            _form[`translations${capitalizeFirstLetter(property)}`] = cleanTranslations(trans);
            setFormObject({ ..._form }, true);
        },
        [setFormObject, form, defaultLanguage, cleanTranslations]
    );

    const reset = React.useCallback(() => {
        setFormObject({ ...initialForm });
        setErrors({});
    }, [setFormObject, setErrors, initialForm]);

    const _onClose = React.useCallback(() => {
        reset();
        if (formEntry) {
            onClose();
            return;
        }
        onCloseAllModals();
    }, [onClose, onCloseAllModals, formEntry, reset]);

    const hasNameTranslations = React.useMemo(() => {
        if (!form.translationsLabel || !Object.keys(form.translationsLabel)) {
            return false;
        }
        return Object.keys(form.translationsLabel).length > 1;
    }, [form]);

    const hasDescriptionTranslations = React.useMemo(() => {
        if (!form.translationsDescription || !Object.keys(form.translationsDescription)) {
            return false;
        }
        return Object.keys(form.translationsDescription).length > 1;
    }, [form]);

    const handleSubmit = React.useCallback(() => {
        if (!formValidate()) {
            return toast.error(t('ticket.form.modal.customFields.error'));
        }
        const tmpFormEntry = formEntry
            ? { ...formEntry.toAPI() }
            : {
                  id: null,
                  usage: usage,
                  weez_field_id: null,
                  required: false,
                  order: null,
                  show_only_bo: false,
                  allow_modification: false,
                  default_value: null,
                  form_field: {}
              };

        Object.keys(form.translationsLabel).forEach(key => {
            if (form.translationsLabel[key] === null) {
                delete form.translationsLabel[key];
            }
        });

        Object.keys(form.translationsDescription).forEach(key => {
            if (form.translationsDescription[key] === null) {
                delete form.translationsDescription[key];
            }
        });

        let _form = {
            is_standard: form.is_standard,
            label: form.label,
            field_type: form.field_type,
            default_value: form.default_value,
            question_choices: form.question_choices,
            description: form.description,
            translations: {
                ...(hasNameTranslations && { label: form.translationsLabel }),
                ...(hasDescriptionTranslations && { description: form.translationsDescription })
            }
        };

        tmpFormEntry.default_value = form.default_value;
        tmpFormEntry.form_field = _form;
        onAddField(FormEntryModel.fromJSON(tmpFormEntry));
        onClose();
        reset();
        toast.success(formEntry ? t('ticket.form.modal.customFields.edit') : t('ticket.form.modal.customFields.success'));
    }, [onAddField, onClose, reset, formValidate, form, usage, formEntry, hasNameTranslations, hasDescriptionTranslations, toast, t]);

    React.useEffect(() => {
        if (formEntry) {
            const obj = {
                translationsLabel: formEntry.form_field.translations.label || {},
                translationsDescription: formEntry.form_field.translations.description || {}
            };
            let _obj = { ...formEntry.form_field.toAPI(), ...obj };

            if (!Object.keys(_obj.translationsLabel).length || !(defaultLanguage in _obj.translationsLabel)) {
                _obj.translationsLabel[defaultLanguage] = _obj.label;
            }

            setFormObject(_obj);
        }
    }, [formEntry]);

    const customTypesOptions = React.useMemo(() => {
        const options = [];
        customTypes.forEach((value, index) => {
            if (!['latitude_longitude', 'address_autocomplete'].includes(value) || permissions.hasCapacity('PyvarCapacity.ADMIN', organization?.id)) {
                options.push({
                    key: index,
                    text: t(`ticket.common.fieldType.${value}`),
                    value: value
                });
            }
        });
        return options;
    }, [customTypes, permissions, organization]);

    const canBeSubmitted = React.useMemo(() => {
        if (form.field_type === 'select' && form.question_choices.length === 0) {
            return false;
        }
        return form.field_type && form.label && !formErrors.label && !formErrors.field_type;
    }, [form, formErrors]);

    return (
        <Modal
            className={Styles['modal-custom-fields']}
            size="large"
            open={showModal}
            onClose={() => {
                _onClose();
                track('ACTION > EXIT CUSTOM FIELD', { 'Page Title': 'CREATE FORM', 'Target type': capitalizeFirstLetter(usage) });
            }}
            title={t('ticket.form.modal.customFields.title')}
            subtitle={t('ticket.form.modal.customFields.subtitle')}
            allowExternalClosing={false}
            customFooter={
                <div className={formEntry ? Styles['modal-custom-fields__footer-edit'] : Styles['modal-custom-fields__footer']}>
                    {!formEntry && (
                        <div
                            className={Styles['modal-custom-fields__return']}
                            onClick={() => {
                                onClose();
                                reset();
                                track('ACTION > RETURN PREDEFINED FIELDS', { 'Page Title': 'CREATE FORM', 'Target type': capitalizeFirstLetter(usage) });
                            }}
                        >
                            <Icon name={'chevron-left'} className={Styles['modal-custom-fields__return-icon']} />
                            <span>{t('ticket.form.modal.customFields.backButton')}</span>
                        </div>
                    )}
                    <div className={Styles['modal-custom-fields__group-buttons']}>
                        <Button
                            className={Styles['modal-custom-fields__cancel-button']}
                            onClick={() => {
                                _onClose();
                                track('ACTION > RETURN', { 'Page Title': 'CREATE FORM', 'Target type': capitalizeFirstLetter(usage), Step: 'Custom' });
                            }}
                            label={t(`ticket.common.action.cancel`)}
                        />
                        <Button
                            primary
                            className={Styles['modal-custom-fields__add-button']}
                            onClick={() => {
                                handleSubmit();
                                track('ACTION > VALIDATE ADD CUSTOM FIELD', {
                                    'Page Title': 'CREATE FORM',
                                    'Target type': capitalizeFirstLetter(usage),
                                    'Field type': form.field_type
                                });
                            }}
                            label={formEntry ? t('ticket.common.action.save') : t('ticket.common.action.add')}
                            disabled={!canBeSubmitted}
                        />
                    </div>
                </div>
            }
        >
            <Form>
                <Form.Field>
                    <Form.Label>{t('ticket.form.modal.customFields.selectType')}</Form.Label>
                    <Select
                        placeholder={t('ticket.common.action.select')}
                        value={form.field_type || null}
                        onChange={value => {
                            //separate set for validation input
                            setForm('field_type', value);
                            setForm('default_value', value === 'checkbox' ? '0' : null);
                            track('ACTION > FIELD TYPE', { 'Page Title': 'CREATE FORM', 'Target type': capitalizeFirstLetter(usage), 'Field type': value });
                        }}
                        options={customTypesOptions}
                    />
                    <Form.FieldErrors errors={[formErrors['field_type'] ? t(formErrors['field_type']) : null]} />
                </Form.Field>
                <FieldNameInput
                    form={form}
                    formErrors={formErrors}
                    defaultLanguage={defaultLanguage}
                    languageOptions={languageOptions}
                    handleLanguage={handleLanguage}
                    handleTranslations={handleTranslations}
                />
                {form.field_type === 'checkbox' && (
                    <Section>
                        <ToggleContainer
                            className={Styles['modal-custom-fields__toggle']}
                            title={t('ticket.form.modal.customFields.toggleTitle')}
                            subtitle={
                                form.default_value === '1' ? t('ticket.form.modal.customFields.toggleSubtitleChecked') : t('ticket.form.modal.customFields.toggleSubtitleUnchecked')
                            }
                            toggle={form.default_value === '1' || false}
                            onToggle={value => setForm('default_value', value ? '1' : '0')}
                        />
                    </Section>
                )}
                {form.field_type === 'select' && <SelectFieldForm form={form} onChangeForm={setForm} />}
                <OptionalSection
                    onToggle={() => {
                        track('ACTION > DISPLAY ADD TEXT', { 'Page Title': 'CREATE FORM', 'Target type': capitalizeFirstLetter(usage) });
                    }}
                    open={form.description !== null}
                    label={t('ticket.form.modal.customFields.descriptionTitle')}
                    optionalText={t('ticket.common.labelOptional')}
                >
                    <FieldDescriptionInput
                        form={form}
                        defaultLanguage={defaultLanguage}
                        languageOptions={languageOptions}
                        handleLanguage={handleLanguage}
                        handleTranslations={handleTranslations}
                    />
                </OptionalSection>
            </Form>
        </Modal>
    );
}

ModalCustomField.propTypes = {
    field: PropTypes.object,
    customTypes: PropTypes.array,
    showModal: PropTypes.bool,
    onClose: PropTypes.func,
    onCloseAllModals: PropTypes.func,
    onAddField: PropTypes.func,
    usage: PropTypes.string
};
